import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ModalService } from '@shared/services/modal/modal.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  AddPersonComponent
} from '@modules/universal-billing/properties/property-profile/tabs/object-resposible/add-person/add-person.component';
import { DatePipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { BaseLoadComponent } from '@shared/components/classes/base-load.component';
import { Observable } from 'rxjs';
import { ContractService } from '@modules/universal-billing/contract/contract.service';
import { RentalContract } from '@modules/universal-billing/contract/contract.interface';
import { MoneyFormatPipe } from '@common/pipe/money-format.pipe';
import { RenterNamePipe } from '@common/pipe/renter-name.pipe';
import {
  ContractDetailsFormComponent
} from '@modules/universal-billing/property-management/contracts/contract-details/forms/contract-details-form/contract-details-form.component';
import {
  ContractPaymentsFormComponent
} from '@modules/universal-billing/property-management/contracts/contract-details/forms/contract-payments-form/contract-payments-form.component';
import {
  ContractSideCostsFormComponent
} from '@modules/universal-billing/property-management/contracts/contract-details/forms/contract-side-costs-form/contract-side-costs-form.component';
import {
  ContractDepositsFormComponent
} from '@modules/universal-billing/property-management/contracts/contract-details/forms/contract-deposits-form/contract-deposits-form.component';
import { LocalizedDatePipe } from "@shared/pipes/localized-date.pipe";
import { EmptyResultComponent } from "@shared/components/empty-result/empty-result.component";
import { ContractRateType, DepositType } from "@modules/universal-billing/contract/contract.enums";
import { CustomNumberPipe } from "@common/pipe/custom-number.pipe";
import { RouterLinkRewriteDirective } from '@common/directive/router-link-rewrite.directive';
import rentTypeGroups from '@common/rent-type-groups';
import { SkeletonModule } from '@primeng/skeleton';
import {
  PaymentsOfContractDetailsComponent
} from '@modules/universal-billing/property-management/contracts/contract-details/components/payments-of-contract-details/payments-of-contract-details.component';
import {
  DetailOfContractDetailsComponent
} from '@modules/universal-billing/property-management/contracts/contract-details/components/detail-of-contract-details/detail-of-contract-details.component';
import {
  SideCostsOfContractDetailsComponent
} from '@modules/universal-billing/property-management/contracts/contract-details/components/side-costs-of-contract-details/side-costs-of-contract-details.component';
import {
  DepositOfContractDetailsComponent
} from '@modules/universal-billing/property-management/contracts/contract-details/components/deposit-of-contract-details/deposit-of-contract-details.component';
import { PaymentsFormComponent } from '@modules/universal-billing/accounting/payments/payments-form/payments-form.component';
import { ModalClose } from '@shared/services/modal/modal.enum';
import urlJoin from 'url-join';
import {environment} from '@environments/environment';
import {ButtonModule} from '@primeng/button';
import {HttpClient} from '@angular/common/http';
import {downloadBlob} from '@shared/utils/file.utils';
import {SweetAlertService} from '@shared/services/sweet-alert.service';
import { DocumentsComponent } from '@modules/universal-billing/accounting/documents/documents.component';

@Component({
  selector: 'app-contract-details',
  standalone: true,
  imports: [
    RouterLink,
    NgForOf,
    TranslatePipe,
    NgClass,
    MoneyFormatPipe,
    NgIf,
    RenterNamePipe,
    LocalizedDatePipe,
    EmptyResultComponent,
    CustomNumberPipe,
    RouterLinkRewriteDirective,
    DatePipe,
    SkeletonModule,
    PaymentsOfContractDetailsComponent,
    DetailOfContractDetailsComponent,
    SideCostsOfContractDetailsComponent,
    DepositOfContractDetailsComponent,
    ButtonModule,
    DocumentsComponent
  ],
  templateUrl: './contract-details.component.html',
  styleUrl: './contract-details.component.scss'
})
export class ContractDetailsComponent extends BaseLoadComponent<RentalContract> implements OnChanges {
  @Input() id: number;

  modalService = inject(ModalService);
  translate = inject(TranslateService);
  contractService = inject(ContractService);
  sweetAlertService = inject(SweetAlertService);
  httpClient = inject(HttpClient);
  loadOnInit = false;
  readonly TABS: any = [
    {
      key: 'DETAILS',
      label: 'INVOICES.DETAILS',
      icon: 'ri-file-text-line',
      component: ContractDetailsFormComponent,
    },
    {
      key: 'ZAHLUNGEN',
      label: 'ACTIONS.PAYMENTS',
      icon: 'ri-money-euro-circle-line',
      component: ContractPaymentsFormComponent
    },
    {
      key: 'SIDECOSTS',
      label: 'CONTRACT.SIDE_COSTS',
      icon: 'ri-money-euro-circle-line',
      component: ContractSideCostsFormComponent
    },
    {
      key: 'DEPOSIT',
      label: 'CONTRACT.DEPOSIT',
      icon: 'ri-money-euro-circle-line',
      component: ContractDepositsFormComponent
    },
    { key: 'DOKUMENTE',
      label: 'PROPERTY.TAB.DOCUMENTS',
      icon: 'ri-clipboard-line' }
  ];

  activeTab: string = 'DETAILS';
  downloadLoading: boolean = false

  protected readonly ContractRateType = ContractRateType;

  setActiveTab(tab: string) {
    this.activeTab = tab;
  }

  downloadContract(id) {
    this.downloadLoading = true;
    this.httpClient.get(urlJoin(environment.apiUrl, `rental-contracts`, id, 'pdf'), { responseType: "blob" }).subscribe({
      next: (blob) => {
        this.downloadLoading = false;
        downloadBlob(blob, `${this.data().contractNumber}.pdf`);
      },
      error: (error) => {
        this.downloadLoading = false;

        if (error.error instanceof Blob) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorText = reader.result as string;
              const errorJson = JSON.parse(errorText);
              const message = errorJson.message || this.translateService.instant('ERROR_DOWNLOADING_CONTRACT');
              this.sweetAlertService.error(message);
            } catch (e) {
              this.sweetAlertService.error(this.translateService.instant('UNKNOWN_ERROR_FORMAT'));
            }
          };
          reader.onerror = () => {
            this.sweetAlertService.error('Error reading error response');
          };
          reader.readAsText(error.error);
        } else {
          const fallback = typeof error.error === 'string'
            ? error.error
            : error?.message || this.translateService.instant('ERROR_DOWNLOADING_CONTRACT');
          this.sweetAlertService.error(fallback);
        }
      }
      ,
      complete: () => {}
    })
  }

  payment() {
    this.modalService.open({
      title: 'PAYMENT.PAYMENT',
      component: PaymentsFormComponent,
      componentOptions: {
        renterId: this.data().renterId,
      },
      modalOptions: {
        size: 'lg',
      }
    }).then((res) => {
      if (res == ModalClose.SUCCESS) {
        this.loadData();
      }
    })
  }

  onEdit() {
    const tab = this.TABS.find(tab => tab.key == this.activeTab);

    this.modalService.open({
      title: tab.label,
      component: tab.component,
      componentOptions: {
        contract: this.data(),
        afterSuccess: this.afterSuccess.bind(this),
        isEdit: true,
      },
      modalOptions: {
        size: 'xl',
        scrollable: this.activeTab != 'DEPOSIT'
      }
    })
  }

  getData(): Observable<any> {
    return this.contractService.getOne(this.id);
  }

  afterSuccess(response: RentalContract, reload = false) {
    if (reload) {
      this.loadData();
    } else {
      this.data.set(response);
    }
  }

  protected readonly DepositType = DepositType;

  ngOnChanges(changes: SimpleChanges): void {
    this.loadData();
  }
}
